import { apiClient } from '../client';

/**
 * Client for `/api/cs/reports/*` (Phase 16 / ADR-026, spec §3.8).
 * Mirrors server/src/modules/cs/reports/reports.service.ts.
 */

export interface WorkloadReport {
  byAssignee: { membershipId: string | null; name: string | null; openTickets: number }[];
  byGroup: { groupId: string | null; groupName: string | null; openTickets: number }[];
  byStatus: { status: string; count: number }[];
}

export async function getWorkloadReport(): Promise<WorkloadReport> {
  const res = await apiClient.get<{ data: WorkloadReport }>('/api/cs/reports/workload');
  return res.data.data;
}

export interface SlaReport {
  windowDays: number;
  totalTickets: number;
  repliedTickets: number;
  medianFirstReplyMinutes: number | null;
  p90FirstReplyMinutes: number | null;
  nudgeCount: number;
  breachCount: number;
}

export async function getSlaReport(days?: number): Promise<SlaReport> {
  const query = days !== undefined ? `?days=${String(days)}` : '';
  const res = await apiClient.get<{ data: SlaReport }>(`/api/cs/reports/sla${query}`);
  return res.data.data;
}

export interface AgentQualityRow {
  membershipId: string;
  name: string | null;
  repliesSent: number;
  ticketsResolved: number;
  /** Null in V1 — reopen tracking needs status history. */
  reopenCount: number | null;
}

export interface AgentQualityReport {
  windowDays: number;
  agents: AgentQualityRow[];
  note: string;
}

export async function getAgentQualityReport(days?: number): Promise<AgentQualityReport> {
  const query = days !== undefined ? `?days=${String(days)}` : '';
  const res = await apiClient.get<{ data: AgentQualityReport }>(`/api/cs/reports/agent-quality${query}`);
  return res.data.data;
}

export interface BehaviorAuditActor {
  membershipId: string;
  name: string | null;
  manualReassignments: number;
  /** True when this actor is > 2σ above the mean across actors. */
  flagged: boolean;
}

export interface BehaviorAuditReport {
  windowDays: number;
  actorCount: number;
  mean: number | null;
  stdDev: number | null;
  actors: BehaviorAuditActor[];
}

export async function getBehaviorAuditReport(days?: number): Promise<BehaviorAuditReport> {
  const query = days !== undefined ? `?days=${String(days)}` : '';
  const res = await apiClient.get<{ data: BehaviorAuditReport }>(`/api/cs/reports/behavior-audit${query}`);
  return res.data.data;
}

export interface CsatReportStub {
  stub: true;
  message: string;
}

export async function getCsatReport(): Promise<CsatReportStub> {
  const res = await apiClient.get<{ data: CsatReportStub }>('/api/cs/reports/csat');
  return res.data.data;
}
